import { useI18n } from '../../i18n';
// ============================================
// Store Selection Page
// Page to select which store to manage
// ============================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import { useStoreSelectionStore } from '../../stores';
import type { Store } from '@conai/shared-types';
import { env } from '../../config/env';
import { extractList } from '../../utils/apiList';
import { readJsonSafely, resolveApiErrorMessage } from '../../utils/http';

export function StoreSelectionPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const {
    availableStores,
    selectedStoreId,
    setAvailableStores,
    setSelectedStore,
    setLoadingStores,
    isLoadingStores,
  } = useStoreSelectionStore();

  const [error, setError] = useState('');

  // Fetch stores on mount
  useEffect(() => {
    const fetchStores = async () => {
      setLoadingStores(true);
      try {
        // Bug #7 (cùng lỗi): thiếu dấu "/" cuối -> 404 HTML -> response.json() ném
        // "... is not valid JSON" thay vì báo lỗi tải cửa hàng.
        const response = await fetch(`${env.apiBaseUrl}/stores/`);
        const data = await readJsonSafely(response);

        if (!response.ok) {
          throw new Error(resolveApiErrorMessage(data, t('storeSelectionLoadError')));
        }

        // Payload có thể là mảng trần, {items}, {results} hoặc {data} tuỳ envelope.
        setAvailableStores(extractList<Store>(data));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Có lỗi xảy ra');
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStores();
  }, [setAvailableStores, setLoadingStores]);

  const handleSelectStore = (store: Store) => {
    setSelectedStore(store);
    navigate(ADMIN_ROUTES.DASHBOARD);
  };

  const handleContinueWithSelected = () => {
    if (selectedStoreId) {
      navigate(ADMIN_ROUTES.DASHBOARD);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 px-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-800">{t('conaiStore')}</h1>
          <p className="mt-2 text-slate-600">{t('storeSelectionSubtitle')}</p>
        </div>

        {/* Store Selection Card */}
        <div className="rounded-2xl bg-white p-8 shadow-xl">
          <h2 className="text-xl font-bold text-slate-900">{t('storeSelectionListTitle')}</h2>
          <p className="mt-2 text-sm text-slate-600">
            {t('storeSelectionListDesc')}
          </p>

          {/* Error Message */}
          {error && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Loading State */}
          {isLoadingStores ? (
            <div className="mt-6 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse rounded-lg border border-slate-200 p-4">
                  <div className="h-5 w-1/3 rounded bg-slate-200" />
                  <div className="mt-2 h-4 w-2/3 rounded bg-slate-200" />
                </div>
              ))}
            </div>
          ) : availableStores.length === 0 ? (
            <div className="mt-6 rounded-lg bg-amber-50 p-4 text-center text-amber-700">
              <p>{t('storeSelectionEmpty')}</p>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              {availableStores.map((store) => (
                <button
                  key={store.id}
                  onClick={() => handleSelectStore(store)}
                  className={`w-full rounded-lg border-2 p-4 text-left transition-all hover:border-blue-500 hover:bg-blue-50 ${
                    selectedStoreId === store.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{store.name}</h3>
                      <p className="mt-1 text-sm text-slate-600">{store.address}</p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                        <span>Mã: {store.code}</span>
                        {store.phone && <span>SĐT: {store.phone}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          store.business_status === 'ACTIVE'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {store.business_status === 'ACTIVE'
                          ? t('active')
                          : t('storeStatusTemporarilyClosed')}
                      </span>
                      {selectedStoreId === store.id && (
                        <svg
                          className="h-5 w-5 text-blue-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Continue Button */}
          {selectedStoreId && (
            <button
              onClick={handleContinueWithSelected}
              className="mt-6 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {t('storeSelectionContinue')}
            </button>
          )}

          {/* Skip for now (AI Admin only) */}
          <button
            onClick={() => navigate(ADMIN_ROUTES.DASHBOARD)}
            className="mt-3 w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Bỏ qua, vào Dashboard
          </button>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-slate-500">
          © 2024 ConAI. All rights reserved.
        </p>
      </div>
    </div>
  );
}
